"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";

const STORAGE_KEY = "opheye-bookmarks";

export interface Bookmark {
  slug: string;
  subspecialty: string;
  title: string;
  addedAt: number;
}

// ── localStorage 헬퍼 ──
function getLocalBookmarks(): Bookmark[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalBookmarks(bookmarks: Bookmark[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
  } catch {
    // storage full or unavailable
  }
}

// ── 서버 API 헬퍼 ──
async function fetchServerBookmarks(): Promise<Bookmark[]> {
  const res = await fetch("/api/bookmarks");
  if (!res.ok) return [];
  const data = await res.json();
  return data.map((b: any) => ({
    slug: b.diseaseSlug,
    subspecialty: b.subspecialty,
    title: b.title,
    addedAt: new Date(b.createdAt).getTime(),
  }));
}

async function addServerBookmark(slug: string, subspecialty: string, title: string) {
  await fetch("/api/bookmarks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, subspecialty, title }),
  });
}

async function removeServerBookmark(slug: string) {
  await fetch("/api/bookmarks", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
}

async function mergeLocalToServer(localBookmarks: Bookmark[]) {
  if (localBookmarks.length === 0) return;
  await fetch("/api/bookmarks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(localBookmarks),
  });
  // 머지 후 localStorage 클리어
  localStorage.removeItem(STORAGE_KEY);
}

// ── 하이브리드 Hook ──
export function useBookmarks() {
  const { data: session, status } = useSession();
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const isLoggedIn = status === "authenticated";
  const mergedRef = useRef(false);

  // 초기 로드
  useEffect(() => {
    if (status === "loading") return;

    if (isLoggedIn) {
      // 서버에서 북마크 로드
      fetchServerBookmarks().then((serverBookmarks) => {
        setBookmarks(serverBookmarks);

        // 첫 로그인 시 localStorage → 서버 머지
        if (!mergedRef.current) {
          mergedRef.current = true;
          const local = getLocalBookmarks();
          if (local.length > 0) {
            mergeLocalToServer(local).then(() => {
              // 머지 후 다시 로드
              fetchServerBookmarks().then(setBookmarks);
            });
          }
        }
      });
    } else {
      setBookmarks(getLocalBookmarks());
    }
  }, [isLoggedIn, status]);

  const addBookmark = useCallback(
    (slug: string, subspecialty: string, title: string) => {
      setBookmarks((prev) => {
        if (prev.some((b) => b.slug === slug)) return prev;
        const next = [...prev, { slug, subspecialty, title, addedAt: Date.now() }];
        if (!isLoggedIn) {
          saveLocalBookmarks(next);
        }
        return next;
      });
      if (isLoggedIn) {
        addServerBookmark(slug, subspecialty, title);
      }
    },
    [isLoggedIn]
  );

  const removeBookmark = useCallback(
    (slug: string) => {
      setBookmarks((prev) => {
        const next = prev.filter((b) => b.slug !== slug);
        if (!isLoggedIn) {
          saveLocalBookmarks(next);
        }
        return next;
      });
      if (isLoggedIn) {
        removeServerBookmark(slug);
      }
    },
    [isLoggedIn]
  );

  const isBookmarked = useCallback(
    (slug: string) => bookmarks.some((b) => b.slug === slug),
    [bookmarks]
  );

  const toggleBookmark = useCallback(
    (slug: string, subspecialty: string, title: string) => {
      if (isBookmarked(slug)) removeBookmark(slug);
      else addBookmark(slug, subspecialty, title);
    },
    [isBookmarked, addBookmark, removeBookmark]
  );

  return { bookmarks, addBookmark, removeBookmark, isBookmarked, toggleBookmark };
}
